import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getOpenApiDocument } from "../../actions";
import { manageableWorkspaces } from "../../utils/permissions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import AppPage from "../app/AppPage";
import { Surface } from "../workspaces/WorkspaceUi";
import ApiKeyBox from "./ApiKeyBox";
import ContentsRail from "./ContentsRail";
import { Code, CodeBlock, CodeTabs, MethodChip, Notice, Prose, SAMPLE_KEY, Section, StatusChip, SubHeading, measure, mono, type } from "./devUi";
import swaggerSkin from "./swaggerSkin";

// swagger-ui is a large bundle. Only a reader who opens this page downloads it.
const SwaggerDocs = React.lazy(() => import("./SwaggerDocs"));

const CONTENTS = [
  { id: "getting-started", label: "Getting started" },
  { id: "authentication", label: "Authentication" },
  { id: "rate-limits", label: "Rate limits" },
  { id: "errors", label: "Errors" },
  { id: "paging", label: "Paging" },
  { id: "endpoints", label: "Endpoints" },
];

// Every refusal the gate in front of /api/v1 can answer, in the reader's words.
const REFUSALS = [
  ["401", "unauthorized", "The key is missing, mistyped or revoked. Every path under /api/v1 that isn't one of ours answers this too."],
  ["402", "quota", "This key has used its requests for the month. It works again next month, or sooner on a larger plan."],
  ["403", "forbidden", "The workspace's plan doesn't include the API, or no rate limit is set on it. Nothing is wrong with the key."],
  ["404", "not-found", "No database or object of that name belongs to this key. Check the name against /databases."],
  ["429", "rate-limit", "Too many requests this minute. Retry-After says how many seconds to wait."],
  ["503", "unavailable", "The database is unreachable right now. Try the same call again shortly."],
];

// What each field of a refusal is for, so nobody has to guess from the JSON.
const REFUSAL_FIELDS = [
  ["success", "Always false on a refusal. A successful answer has no such field: it is the data itself."],
  ["reason", "A short label to branch on in code. It never changes wording between releases."],
  ["msg", "One sentence for a person, saying what to do next. Safe to show in your own interface."],
  ["reference", "The same six characters as X-Request-Id. Quote it if you ask us about the call."],
];

const HEADERS = [
  ["X-RateLimit-Limit", "How many requests this key may make in a minute."],
  ["X-RateLimit-Remaining", "How many of them are left. Watch this rather than counting your own."],
  ["Retry-After", "On a 429 only: how many seconds until the next request is allowed."],
  ["X-Request-Id", "Six characters naming this call in our logs. On every answer, refused or not."],
];

const samples = (base, key, database) => {
  const shown = key || SAMPLE_KEY;
  return [
    {
      label: "curl",
      language: "bash",
      code: `curl -H "Authorization: Bearer ${shown}" \\\n  "${base}/databases"\n\ncurl -H "Authorization: Bearer ${shown}" \\\n  "${base}/databases/${database}/search?q=order&limit=20"`,
    },
    {
      label: "JavaScript",
      language: "javascript",
      code: `const key = "${shown}";\nconst base = "${base}";\nconst headers = { Authorization: \`Bearer \${key}\` };\n\nconst res = await fetch(\`\${base}/databases\`, { headers });\nif (!res.ok) throw new Error((await res.json()).msg);\n\nconst databases = await res.json();\nconsole.log(databases); // [{ name, label, objects }]`,
    },
    {
      label: "Python",
      language: "python",
      code: `import requests\n\nkey = "${shown}"\nbase = "${base}"\nheaders = {"Authorization": f"Bearer {key}"}\n\nres = requests.get(f"{base}/databases", headers=headers)\nif not res.ok:\n    raise SystemExit(res.json()["msg"])\n\nfor database in res.json():\n    print(database["name"], database["objects"])`,
    },
  ];
};

const pagingSample = (base, database) =>
  `let cursor = null;\ndo {\n  const url = new URL(\`${base}/databases/${database}/search\`);\n  url.searchParams.set("limit", "100");\n  if (cursor) url.searchParams.set("cursor", cursor);\n\n  const page = await (await fetch(url, { headers })).json();\n  page.results.forEach((object) => console.log(object.id, object.name));\n\n  cursor = page.nextCursor; // null on the last page\n} while (cursor);`;

const backoffSample = `const answer = await fetch(url, { headers });\n\nif (answer.status === 429) {\n  const seconds = Number(answer.headers.get("Retry-After")) || 1;\n  await new Promise((wake) => setTimeout(wake, seconds * 1000));\n  return call(url); // one wait, then the same call again\n}`;

const REFUSAL_SHAPE = `{\n  "success": false,\n  "reason": "unauthorized",\n  "msg": "This API key isn't valid. Create a new one in Workspace -> API keys.",\n  "reference": "a3f19c"\n}`;

// The reference's own contents: what the document the server generated holds,
// read as a list before the renderer that can call each one arrives.
const endpointsOf = (spec) => {
  const paths = (spec && spec.paths) || {};
  const out = [];
  Object.keys(paths).forEach((path) => {
    Object.keys(paths[path] || {}).forEach((method) => {
      const operation = paths[path][method] || {};
      out.push({ method, path, summary: operation.summary || "" });
    });
  });
  return out;
};

// A small definition list: a name on the left, what it is on the right, one
// hairline between rows rather than a rule under each column.
function Defined({ rows }) {
  return (
    <Box component="dl" sx={{ m: 0 }}>
      {rows.map(([name, meaning], index) => (
        <Box
          key={name}
          sx={{
            display: { xs: "block", sm: "flex" },
            gap: 3,
            py: 1.75,
            borderTop: index === 0 ? "none" : `1px solid ${brand.hairline}`,
            "&:first-of-type": { pt: 0 },
            "&:last-of-type": { pb: 0 },
          }}
        >
          <Box
            component="dt"
            sx={{ fontFamily: `${mono} !important`, fontSize: "0.82rem", color: brand.inkSoft, flex: "0 0 auto", width: { sm: 168 }, mb: { xs: 0.35, sm: 0 } }}
          >
            {name}
          </Box>
          <Box component="dd" sx={{ m: 0, fontSize: "0.925rem", lineHeight: 1.65, color: brand.prose, maxWidth: measure }}>
            {meaning}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// A point worth setting apart from the prose. It borrows the page's notice
// shape, so an aside and a refusal are told apart by tone rather than by two
// different frames.
function Note({ title, children, tone = notice.quiet, icon = <InfoOutlinedIcon fontSize="small" /> }) {
  return (
    <Notice tone={tone} icon={icon} title={title}>
      {children}
    </Notice>
  );
}

// One thing the API promises, set beside a mark rather than a bullet.
function Assurance({ children }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        aria-hidden
        sx={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", bgcolor: notice.good.bg, color: notice.good.fg, mt: "1px" }}
      >
        <CheckRoundedIcon sx={{ fontSize: 14 }} />
      </Box>
      <Typography sx={{ ...type.prose, fontSize: "0.94rem" }}>{children}</Typography>
    </Stack>
  );
}

// Everything a developer needs in one page: what the API is for, where the key
// comes from, proof that their key works, how to send it, what a refusal means,
// and every endpoint with a box to try it against their own data.
export default function DevelopersPage({ user }) {
  const [spec, setSpec] = useState(undefined);
  const [verified, setVerified] = useState(null);
  // The renderer reads the key when it sends a request, not when it mounts.
  const keyRef = useRef("");

  useEffect(() => {
    let live = true;
    getOpenApiDocument().then((document) => live && setSpec(document));
    return () => {
      live = false;
    };
  }, []);

  const onVerified = useCallback((key, databases) => {
    keyRef.current = key;
    setVerified({ key, databases });
  }, []);

  const onForget = useCallback(() => {
    keyRef.current = "";
    setVerified(null);
  }, []);

  const getApiKey = useCallback(() => keyRef.current, []);

  const origin = typeof window !== "undefined" && window.location ? window.location.origin : "";
  const base = spec?.servers?.[0]?.url || `${origin}/api/v1`;
  const database = verified?.databases?.[0]?.name || "your-database";
  const endpoints = useMemo(() => endpointsOf(spec), [spec]);
  const canManage = (manageableWorkspaces(user) || []).length > 0;

  const keysLink = canManage ? (
    <Link component={RouterLink} to="/workspace">
      Workspace → API keys
    </Link>
  ) : (
    <Code>Workspace → API keys</Code>
  );

  return (
    <AppPage
      title="Developers"
      description="Read your process library from your own systems, with a key your workspace created."
      maxWidth={1044}
      actions={
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1,
            border: `1px solid ${verified ? notice.good.line : brand.line}`,
            bgcolor: verified ? notice.good.bg : "#fff",
            borderRadius: "999px",
            px: 1.75,
            py: 0.75,
            transition: "background-color 180ms, border-color 180ms",
          }}
        >
          <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: verified ? notice.good.fg : "#c8ccd6" }} />
          <Typography sx={{ fontSize: "0.79rem", fontWeight: verified ? 600 : 400, color: verified ? notice.good.fg : brand.muted }}>
            {verified ? "Key checked" : "No key checked yet"}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "204px minmax(0, 1fr)" }, columnGap: { md: 7 } }}>
        <ContentsRail sections={CONTENTS} />

        <Box sx={{ minWidth: 0 }}>
          <Section
            id="getting-started"
            title="Getting started"
            subtitle="Three minutes from a key in your hand to a call that answers."
          >
            <Prose sx={{ mb: 2 }}>
              The Business Flows API reads the same process library this app shows: the databases your workspace owns, the object types and
              relationships they show, a search across them, one object with everything it is related to, what sits beneath it, and its diagram as a
              file. Use it to bring your models into a wiki, a report, a portal or a script. Every answer is JSON — except a diagram, which comes back
              as the file itself.
            </Prose>
            <Prose sx={{ mb: 3 }}>
              No key yet? An owner or admin of your workspace creates one under {keysLink}. It is shown once, reads only the databases that workspace
              owns, and can be revoked at any time.
            </Prose>

            <ApiKeyBox onVerified={onVerified} onForget={onForget} />

            <Box sx={{ mt: 4 }}>
              <SubHeading>Your first call</SubHeading>
              <Prose sx={{ mb: 1.75, fontSize: "0.92rem" }}>
                {verified
                  ? "Copy and run — these examples already carry the key you checked, and your first database."
                  : "Copy and run. Check a key above and these examples fill themselves in with it."}
              </Prose>
              <CodeTabs samples={samples(base, verified?.key, database)} />
              <Prose sx={{ mt: 1.75, fontSize: "0.92rem" }}>
                The base address is <Code>{base}</Code>. Names in a path — the database, an object's id — are the ones <Code>/databases</Code> and the
                search answer give you.
              </Prose>
            </Box>
          </Section>

          <Section id="authentication" title="Authentication" subtitle="One header, no cookie, no login.">
            <CodeBlock
              code={`GET ${base}/databases\nAuthorization: Bearer ${verified?.key || SAMPLE_KEY}`}
              language="http"
              caption="Every request"
              label="Copy the header"
            />
            <Stack spacing={1.5} sx={{ mt: 3 }}>
              <Assurance>
                <strong>A key belongs to a workspace, not to you.</strong> It reads the databases that workspace owns, and nothing else on the server.
                Anyone holding it can read them, so send it from your own server rather than from a page in someone's browser.
              </Assurance>
              <Assurance>
                <strong>It is read-only.</strong> Nothing in this API writes, archives or changes anything, whoever created the key.
              </Assurance>
              <Assurance>
                <strong>What an admin hides stays hidden.</strong> An object type or relationship hidden in Views is absent here too — from{" "}
                <Code>/types</Code>, from search results, from an object's relationships and from what a structure map lists under <Code>/parts</Code>.
                The API is not a way around the configuration your workspace chose.
              </Assurance>
            </Stack>
          </Section>

          <Section id="rate-limits" title="Rate limits" subtitle="Two numbers from your plan, and the headers that count them down.">
            <Prose sx={{ mb: 2.5 }}>
              Your plan sets how many requests a key may make in a month, and how many it may make in one minute — per key, per minute, so one busy
              script never spends another key's allowance. A month's worth gone answers <StatusChip status="402" />; a minute's worth gone answers{" "}
              <StatusChip status="429" /> and starts again the next minute.
            </Prose>
            <Surface sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5, borderRadius: "16px" }}>
              <Defined rows={HEADERS} />
            </Surface>
            <SubHeading>When a minute runs out</SubHeading>
            <Prose sx={{ mb: 1.75, fontSize: "0.92rem" }}>
              Wait the number of seconds the answer names, then send the same request again. One wait is enough — the count is a rolling minute, not a
              penalty.
            </Prose>
            <CodeBlock code={backoffSample} language="javascript" caption="Waiting out a limit" label="Copy the retry example" />
          </Section>

          <Section id="errors" title="Errors" subtitle="Every refusal is JSON, names itself, and says what to do next.">
            <SubHeading sx={{ mb: 1.5 }}>When a call is refused</SubHeading>
            <Surface sx={{ overflow: "hidden", borderRadius: "16px" }}>
              {REFUSALS.map(([status, reason, meaning], index) => (
                <Stack
                  key={status}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.75, sm: 2 }}
                  sx={{
                    px: { xs: 2, sm: 2.75 },
                    py: 2,
                    borderTop: index === 0 ? "none" : `1px solid ${brand.hairline}`,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: { sm: 150 }, flexShrink: 0 }}>
                    <StatusChip status={status} />
                    <Typography component="span" sx={{ fontFamily: `${mono} !important`, fontSize: "0.8rem", color: brand.muted }}>
                      {reason}
                    </Typography>
                  </Stack>
                  <Typography sx={{ ...type.prose, fontSize: "0.925rem" }}>{meaning}</Typography>
                </Stack>
              ))}
            </Surface>

            <Box sx={{ mt: 3 }}>
              <SubHeading>What a refusal looks like</SubHeading>
              <Prose sx={{ mb: 1.75, fontSize: "0.92rem" }}>Four fields, the same four every time.</Prose>
              <CodeBlock code={REFUSAL_SHAPE} language="json" caption="401 unauthorized" label="Copy the shape of a refusal" />
              <Surface sx={{ p: { xs: 2, sm: 2.5 }, mt: 2, borderRadius: "16px" }}>
                <Defined rows={REFUSAL_FIELDS} />
              </Surface>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Note title="A 401 that isn't always about your key" tone={notice.fix} icon={<ErrorOutlineRoundedIcon fontSize="small" />}>
                Only <Code>/api/v1/openapi.json</Code> is open without a key. Every other path under <Code>/api/v1</Code> answers 401 — including a
                path that doesn't exist, which the gate refuses before the router ever sees it. So if a call you expect to work is refused, check the
                path against the reference below before you doubt the key.
              </Note>
            </Box>
          </Section>

          <Section id="paging" title="Paging" subtitle="Search and parts answer one page at a time, with a cursor to the next.">
            <CodeBlock code={pagingSample(base, database)} language="javascript" caption="Walking every page" label="Copy the paging example" />
            <Prose sx={{ mt: 1.75, fontSize: "0.92rem" }}>
              A page carries <Code>results</Code> (or <Code>parts</Code>), the <Code>total</Code> behind it, and <Code>nextCursor</Code>. Pass that
              cursor back as the <Code>cursor</Code> parameter for the next page; it is <Code>null</Code> on the last one. <Code>limit</Code> sets the
              page size. A cursor that isn't one of ours answers <StatusChip status="400" /> rather than quietly starting again.
            </Prose>
          </Section>

          <Section id="endpoints" title="Endpoints" subtitle="Every call this API serves, and a box to run one against your own data.">
            <Surface sx={{ mb: 3, p: { xs: 2, sm: 2.5 }, borderRadius: "16px" }}>
              {spec === undefined && (
                <Stack spacing={1.25}>
                  {[0, 1, 2, 3].map((row) => (
                    <Skeleton key={row} variant="rounded" height={20} sx={{ borderRadius: "6px", maxWidth: `${78 - row * 7}%` }} />
                  ))}
                </Stack>
              )}
              {spec === null && (
                <Typography sx={{ ...type.prose, fontSize: "0.92rem" }}>
                  The list of endpoints comes from the API description, which didn't arrive. It is named again below, with what to do about it.
                </Typography>
              )}
              {endpoints.length > 0 && (
                <>
                  <Typography sx={{ ...type.meta, mb: 1.75 }}>
                    {endpoints.length} endpoints, all of them GET. Each one opens below, with its parameters and a box to run it.
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3, rowGap: 1 }}>
                    {endpoints.map(({ method, path }) => (
                      <Stack key={`${method} ${path}`} direction="row" spacing={1.25} alignItems="baseline" sx={{ minWidth: 0 }}>
                        <MethodChip method={method} />
                        <Typography
                          component="span"
                          sx={{ fontFamily: `${mono} !important`, fontSize: "0.82rem", color: brand.ink, wordBreak: "break-word" }}
                        >
                          {path}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </>
              )}
            </Surface>

            <Note title="Bring them into your own tools">
              <Code>{`${base}/openapi.json`}</Code> is the OpenAPI 3.0 description of every endpoint above, open without a key. Import that address into
              Postman or Insomnia — or feed it to a client generator — and every call arrives ready to send; add your key as the bearer token.
            </Note>

            <Surface sx={{ borderRadius: "16px", mt: 3 }}>
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  borderBottom: `1px solid ${brand.line}`,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: 1,
                }}
              >
                <Typography variant="h3" component="h3" sx={{ ...type.subTitle }}>
                  Try each endpoint
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ ...type.meta, color: verified ? brand.ink : brand.muted, fontWeight: verified ? 600 : 400 }}>
                  {verified ? "Your checked key is sent with every try-it call." : "Check a key above and try-it calls are signed for you."}
                </Typography>
              </Box>

              {spec === undefined && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack spacing={1.25}>
                    <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                    <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                    <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                  </Stack>
                </Box>
              )}

              {spec === null && (
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <Note title="We couldn't load the API description just now" tone={notice.fix} icon={<ErrorOutlineRoundedIcon fontSize="small" />}>
                    The reference needs the document the server generates at <Code>{`${base}/openapi.json`}</Code>. Reload the page; if it still
                    doesn't arrive, the examples above work exactly as they are, and we'd like to hear about it.
                  </Note>
                </Box>
              )}

              {spec && (
                <Box sx={swaggerSkin}>
                  <Suspense
                    fallback={
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Stack spacing={1.25}>
                          <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                          <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                          <Skeleton variant="rounded" height={44} sx={{ borderRadius: "14px" }} />
                        </Stack>
                      </Box>
                    }
                  >
                    <SwaggerDocs spec={spec} getApiKey={getApiKey} />
                  </Suspense>
                </Box>
              )}
            </Surface>
          </Section>

          <Typography sx={{ ...type.meta, borderTop: `1px solid ${brand.line}`, pt: 2 }}>
            Version 1 of the API. Endpoints are added, never quietly changed; anything that would break a call you already make arrives as a new
            version.
          </Typography>
        </Box>
      </Box>
    </AppPage>
  );
}
